<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Icon } from '@iconify/vue'
import BaseDialog from './BaseDialog.vue'
import UnifiedCredentialsManager from '../credentials/UnifiedCredentialsManager.vue'
import { useAppModalPriority } from '~/compositions/useAppModalPriority'
import { recordEvent } from '~/observability/userEvents'
import { useConnectionsStore } from '~/stores/connections'
import { useUserStore } from '~/stores/user'
import { BRAND_NAME } from '~/constants/branding'

const setupStep = ref<'intro' | 'credentials' | 'completed'>('intro')
const connectionsStore = useConnectionsStore()
const userStore = useUserStore()
// Owned by `useAppModalPriority` so `App.vue` can yield to this dialog on
// exactly the conditions that actually put it on screen, rather than on a
// hand-copied subset of them
const { shouldShowConnectionOnboarding: shouldShow } = useAppModalPriority()

const dialogOpen = ref(false)
// Cache computed values to avoid repeated store access
const remainingConnectionsCount = computed(
  () => connectionsStore.remainingConnectionsCount
)
const setupConnectionTypeCount = computed(
  () => connectionsStore.setupConnectionTypeMetas.length
)
const setupConnectionTypes = computed(() => {
  return new Set(connectionsStore.setupConnectionTypeMetas.map((c) => c.type))
})
// Only compute labels when actually needed (lazy evaluation)
const remainingConnectionLabels = computed(() => {
  const count = connectionsStore.remainingConnectionsCount
  if (count === 0 || connectionsStore.connections.length === 0) return []

  const handledTypes = new Set(
    [
      ...connectionsStore.validatedConnections,
      ...connectionsStore.notUsedConnections,
      ...connectionsStore.disabledConnections,
    ]
      .filter((c) => setupConnectionTypes.value.has(c.type))
      .map((c) => c.type)
  )

  return connectionsStore.setupConnectionTypeMetas
    .filter((c) => !handledTypes.has(c.type))
    .map((c) => c.label)
})

const remainingConnectionsMessage = computed(() => {
  const count = remainingConnectionsCount.value
  if (count === 0) return ''

  const total = setupConnectionTypeCount.value
  const labels = remainingConnectionLabels.value
  const subject =
    labels.length > 0 && labels.length <= 3 ? formatList(labels) : String(count)
  const plural = count > 1

  return `${subject} ${plural ? 'connections' : 'connection'} still ${plural ? 'need' : 'needs'} to be configured (${count} of ${total} total).`
})

// Extract condition to avoid repeated store access in template
const hasRemainingConnections = computed(() => {
  return (
    setupConnectionTypeCount.value > 0 && remainingConnectionsCount.value > 0
  )
})
function formatList(items: string[]): string {
  if (items.length === 1) return items[0]
  if (items.length === 2) return `${items[0]} and ${items[1]}`
  return `${items.slice(0, -1).join(', ')}, and ${items.at(-1)}`
}

watch(
  shouldShow,
  (shouldShowValue) => {
    if (shouldShowValue) {
      setupStep.value = 'intro'
      dialogOpen.value = true
    } else if (setupStep.value === 'credentials') {
      // The user was entering credentials and the last one just landed — this
      // is the genuine finish, and the 🎉 step is its ending
      setupStep.value = 'completed'
    } else {
      // The dialog no longer applies to this user at all (it was opened before
      // `userInfo` hydrated, another tab completed the setup, the org changed).
      // Congratulating them for setup they never did is worse than nothing.
      dialogOpen.value = false
    }
  },
  { immediate: true }
)

// Load connections before displaying the intro step so we have accurate counts.
// Gated on `sessionReady` rather than mount time: this component mounts as
// soon as `loggedIn` flips true, before `userInfo` (and thus `isSubCarrier`
// etc.) hydrates, so checking those role flags in `onMounted` would read
// their default `false` and fetch connections for roles that must not.
watch(
  () => userStore.sessionReady,
  async (ready) => {
    if (!ready) return
    if (
      !userStore.isSubCarrier &&
      !userStore.isUTASOnlyOrg &&
      !userStore.isTerminalOrg
    ) {
      await connectionsStore.loadConnectionsIfNotLoaded()
    }
  },
  { immediate: true }
)

function handleSetupNow() {
  recordEvent('terminalSetupStarted')
  setupStep.value = 'credentials'
}

async function handleSkipLater() {
  recordEvent('terminalSetupSkippedLater')
  await userStore.handleSetupSkipped('later')
  dialogOpen.value = false
}

async function handleNeverShow() {
  recordEvent('terminalSetupSkippedNever')
  await userStore.handleSetupSkipped('never')
  dialogOpen.value = false
}

function handleBackToIntro() {
  setupStep.value = 'intro'
}
</script>

<template>
  <BaseDialog
    v-if="dialogOpen"
    v-model="dialogOpen"
    :close-on-click-modal="false"
    :max-width="1000"
    :proportional-width="0.75"
    class="terminal-setup-dialog"
  >
    <template #header>
      <div class="w-full px-6 py-4 text-center">
        <h2 class="text-2xl font-bold text-gray-900">
          {{
            setupStep === 'completed'
              ? `Welcome to ${BRAND_NAME}!`
              : `Welcome to ${BRAND_NAME}, Let's Set You Up!`
          }}
        </h2>
      </div>
    </template>
    <template #footer>
      <!-- Intro Step Footer -->
      <div v-if="setupStep === 'intro'" class="flex gap-1 w-full px-6">
        <el-button
          size="large"
          class="flex-1"
          type="danger"
          plain
          @click="handleNeverShow"
        >
          <Icon icon="mdi:bell-off" class="mr-2" />
          Don't Show This Again
        </el-button>
        <el-button size="large" class="flex-1" @click="handleSkipLater">
          <Icon icon="mdi:clock-outline" class="mr-2" />
          I'll Do This Later
        </el-button>
        <el-button
          size="large"
          class="flex-1"
          type="primary"
          @click="handleSetupNow"
        >
          <Icon icon="mdi:lightning-bolt" class="mr-1" />
          Set up Credentials
        </el-button>
      </div>

      <!-- Credentials Step Footer -->
      <div
        v-else-if="setupStep === 'credentials'"
        class="flex items-center gap-3 w-full"
      >
        <div class="flex items-center gap-3 px-6 flex-1">
          <el-button size="large" @click="handleBackToIntro">
            ← Back
          </el-button>
          <div class="flex-1"></div>
        </div>
        <el-button size="large" @click="dialogOpen = false">Close</el-button>
      </div>

      <!-- Completed Step Footer - Empty to prevent default cancel button -->
      <div v-else></div>
    </template>
    <!-- Intro Step -->
    <div
      v-if="setupStep === 'intro'"
      class="text-center space-y-6 pt-4 pb-2 px-6"
    >
      <div>
        <img
          src="../../assets/logo_outline.svg"
          :alt="`${BRAND_NAME} Logo`"
          class="h-16 mx-auto mb-8"
        />
      </div>

      <div class="text-lg space-y-4">
        <p class="text-gray-700 mb-4">
          To get the most out of {{ BRAND_NAME }}, you'll need to connect your
          terminal and line credentials. <br />⚠️ {{ BRAND_NAME }} is not useful
          unless you complete this step.
        </p>
        <div
          v-if="hasRemainingConnections"
          class="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6"
        >
          <p class="text-red-600 font-semibold">
            {{ remainingConnectionsMessage }}
          </p>
        </div>
      </div>
    </div>

    <!-- Credentials Step -->
    <div v-if="setupStep === 'credentials'" class="pt-4 pb-2 mb-6">
      <h2 class="text-2xl font-semibold text-gray-900">
        Terminal & Line Credentials
      </h2>
      <UnifiedCredentialsManager context="setup" />
    </div>

    <!-- Completed Step -->
    <div
      v-if="setupStep === 'completed'"
      class="text-center space-y-6 pt-8 pb-4 px-6"
    >
      <div class="text-6xl mb-4">🎉</div>
      <h3 class="text-2xl font-semibold text-green-600">Setup Complete!</h3>
      <p class="text-gray-700">
        You're all set to use {{ BRAND_NAME }} effectively. Welcome to the pack!
      </p>
      <el-button size="large" @click="dialogOpen = false">Close</el-button>
    </div>
  </BaseDialog>
</template>
